import axios from "axios";
import * as Spacekit from "spacekit.js";
import { THREE } from "spacekit.js";
import moment from "moment";
import HUDService from "@/services/hud.service";
import ObjectsService from "./objects.service";
import PopupService from "@/services/popup.service";
import SpacekitService from "@/services/spacekit.service";
import SynodicChartService from "@/services/synodic-chart.service";
import SyntheticObjectsService from "./synthetic-objects.service";
import TimeFrameService from "@/services/timeframe.service";
import UtilsService from "@/services/utils.service";
import VisualisationService from "@/services/visualisation.service";
import store from "@/store";
import CommonGetters from "@/store/common/common-getters";
import CommonActions from "@/store/common/common-actions";
import Keys from "@/constants/keys";
import Config from "@/constants/config";
import LayerService from "@/services/layer.service";

const SynodicCalculationService = {
    EARTH_PERIOD: 365.25,
    EARTH_ROTATION_AXIS_ANGLE: 23.4,
    PROPAGATION_TIME_STEP_DAY: 1,
    DETECTION_POLAR_STEP_ANGLE: 1,
    AVERAGED_EARTH_ORBIT_RADIUS: 1,
    AVERAGED_EARTH_ORBIT_STEP_ANGLE: 1,
    EARTH_ON_SCALE_FACTOR: 0.006692096026745154,
    EARTH_ON_SCALE_ASTEROID_FACTOR: 0.000852708,
    ASTEROID_ZOOM_FACTOR: 0.4,
    TRANSFORMED_EARTH_OBJECT_NAME: "earth_transformed",
    TRANSFORMED_SPACE_OBJECT_SUFFIX: "_transformed",
    TRANSFORMED_SPACE_OBJECT_SUFFIX_PERTURBED: "_transformed_perturbed",
    EARTH_PLACEHOLDER_NAME: "earth_placeholder",
    EARTH_FAKE_MOON_NAME: "earth_fake_moon",
    LABEL_COLOR: 0xffffff,
    LABEL_SIZE: 13,
    EARTH_OBJECT_DEFAULT_OPTIONS: {
        ...Spacekit.SpaceObjectPresets.EARTH,
        name: "Earth",
        textureUrl: "/static/textures/detailed/earth.jpg",
        radius: 0.0063710084,
        rotation: {
            enable: false,
        },
    },
    FAKE_MOON_DEFAULT_OPTIONS: {
        ephemData: {
            // Define orbit shape
            a: 0.43273864224742586 / 2, // Radius of the vertical cone, calculated from the cone formulae, for h=0.5au
            e: 0,
            i: Math.PI, // Flip of the orbit rotation direction

            // Define the orientation of the orbit
            om: -Math.PI / 2, // Rotate reference system to make it synodic (as other elements)
            w: 0,
            ma: 0,

            // Object init position
            epoch: 2451623.815972, // 20.03.2000 07:35 as a starting position, X-axis 90[deg], Y-axis 23.4[deg] to the screen
            period: 365.242189, // Tropical year period
        },
        data: {
            textureUrl: "/static/textures/particle.png",
            radius: 0.005,
        },
    },
    PLANETS_DEFAULT_OPTIONS: [
        {
            ...Spacekit.SpaceObjectPresets.MERCURY,
            name: "Mercury",
            textureUrl: "/static/textures/detailed/mercury.jpg",
            radius: 0.0024394,
            rotation: { enable: true, period: 58.646, lambdaDeg: 252.2509 },
        },
        {
            ...Spacekit.SpaceObjectPresets.VENUS,
            name: "Venus",
            textureUrl: "/static/textures/detailed/venus.jpg",
            radius: 0.0060518,
            rotation: {
                enable: true,
                period: -243.018,
                lambdaDeg: 181.9798,
            },
        },
        {
            ...Spacekit.SpaceObjectPresets.MARS,
            name: "Mars",
            textureUrl: "/static/textures/detailed/mars.jpg",
            radius: 0.0033895,
            rotation: { enable: true, period: 1.026, lambdaDeg: 355.4329 },
        },
        {
            ...Spacekit.SpaceObjectPresets.JUPITER,
            name: "Jupiter",
            textureUrl: "/static/textures/detailed/jupiter.jpg",
            radius: 0.069911,
            rotation: { enable: true, period: 0.41354, lambdaDeg: 34.3515 },
        },
        {
            ...Spacekit.SpaceObjectPresets.SATURN,
            name: "Saturn",
            textureUrl: "/static/textures/detailed/saturn.jpg",
            radius: 0.058232,
            rotation: { enable: true, period: 0.444, lambdaDeg: 50.0774 },
        },
        {
            ...Spacekit.SpaceObjectPresets.URANUS,
            name: "Uranus",
            textureUrl: "/static/textures/detailed/uranus.jpg",
            radius: 0.025362,
            rotation: { enable: true, period: -0.718, lambdaDeg: 314.055 },
        },
        {
            ...Spacekit.SpaceObjectPresets.NEPTUNE,
            name: "Neptune",
            textureUrl: "/static/textures/detailed/neptune.jpg",
            radius: 0.024622,
            rotation: { enable: true, period: 0.671, lambdaDeg: 304.3486 },
        },
    ],
    TRANSFORMED_PLANETS_NAMES: [
        "mercury_transformed",
        "venus_transformed",
        "mars_transformed",
        "jupiter_transformed",
        "saturn_transformed",
        "uranus_transformed",
        "neptune_transformed",
    ],
    ORBIT_RANGE_COLOR: "#ff6400",

    async createSynodicOrbit(objectData, ephemData, designator) {
        const {
            isObjectSelected,
            isObjectVisible,
        } = SynodicCalculationService.getSynodicObjectVisibilityInfo(
            designator
        );
        const spaceObjectOrbitData = SpacekitService.getSpaceObjectData(
            ephemData
        );
        if (!spaceObjectOrbitData) {
            return false;
        }
        const isPerturbedOrbit = SynodicCalculationService.getPerturbedSynodicOrbitList().includes(
            designator
        );
        if (isObjectSelected) {
            SynodicCalculationService.createOrbitRange(objectData, spaceObjectOrbitData);
            SynodicCalculationService.createDetectionPolarObjects(objectData);
        }
        if (isObjectVisible) {
            SpacekitService.handleSynodicOrbitHiglight();
            isObjectSelected && SynodicCalculationService.setActiveObject(designator);
            return true;
        }
        if (!isPerturbedOrbit) {
            SynodicCalculationService.createSynodicOrbitObject(ephemData, objectData, spaceObjectOrbitData, designator);
            return true;
        }
        if (isPerturbedOrbit) {
            return await SynodicCalculationService.createSynodicPerturbedOrbitObject(ephemData, objectData, designator);
        }
    },

    createOrbitRange(objectData, spaceObjectData) {
        SpacekitService.removeOrbitRangeObject();
        const { perihelionDistance, aphelionDistance } = objectData;
        const { minZValue, maxZValue } = SynodicCalculationService.getOrbitRangeLimitValues(spaceObjectData);
        const perihelionCircumferenceData = SynodicCalculationService.calculateOrbitRangeEdge(
            perihelionDistance,
            minZValue,
            maxZValue
        );
        const aphelionCircumferenceData = SynodicCalculationService.calculateOrbitRangeEdge(
            aphelionDistance,
            minZValue,
            maxZValue
        );
        SpacekitService.addOrbitRange(perihelionCircumferenceData, aphelionCircumferenceData);
    },

    getOrbitRangeLimitValues(spaceObjectData) {
        const objectOrbitZPoints = spaceObjectData.getOrbit().orbitPoints.map(({ z }) => z);
        return {
            minZValue: Math.min(...objectOrbitZPoints),
            maxZValue: Math.max(...objectOrbitZPoints),
        };
    },

    calculateOrbitRangeEdge(distance, minZ, maxZ) {
        return [
            [distance, minZ],
            [distance, maxZ],
        ];
    },

    createDetectionPolarObjects(objectData) {
        const detectionPolarCoordinates = [];
        for (let i = 0; i <= 179; i += SynodicCalculationService.DETECTION_POLAR_STEP_ANGLE) {
            const pointCoordinates = SynodicCalculationService.calculatePolarPlotCoordinates(i, objectData);
            detectionPolarCoordinates.push(pointCoordinates);
        }
        SynodicCalculationService.setDetectionPolarPoints(detectionPolarCoordinates);
        SpacekitService.calculateDetectionPolarObject(objectData);
    },

    calculatePolarPlotCoordinates(phaseAngleDeg, objectData) {
        const { absoluteMagnitude, magnitudeLimit, slopeParameter } = objectData;

        const phaseAngleTangens = Math.tan((phaseAngleDeg * Math.PI) / 180 / 2);
        const phi1 = Math.exp(-3.33 * Math.pow(phaseAngleTangens, 0.63));
        const phi2 = Math.exp(-1.87 * Math.pow(phaseAngleTangens, 1.22));
        const apparentMagnitude =
            absoluteMagnitude - 2.5 * Math.log10((1 - slopeParameter) * phi1 + slopeParameter * phi2);
        const productDistance = Math.pow(10, (parseFloat(magnitudeLimit) - apparentMagnitude) / 5);
        const k2Param = 1 + 2 * productDistance * Math.cos((phaseAngleDeg * Math.PI) / 180);
        const d1Param =
            k2Param > 2 * productDistance
                ? Math.sqrt((k2Param + Math.sqrt(k2Param ** 2 - 4 * productDistance ** 2)) / 2)
                : 0;
        const r1Param = k2Param > 2 * productDistance ? productDistance / d1Param : 0;
        const del1Param =
            k2Param > 2 * productDistance
                ? (Math.asin(r1Param * Math.sin((phaseAngleDeg * Math.PI) / 180)) * 180) / Math.PI
                : 0;
        const beta1Param = k2Param > 2 * productDistance ? phaseAngleDeg + del1Param : 0;
        const y1Coord = k2Param > 2 * productDistance ? 1 + r1Param * Math.cos((beta1Param * Math.PI) / 180) : null;
        const x1Coord = k2Param > 2 * productDistance ? r1Param * Math.sin((beta1Param * Math.PI) / 180) : null;

        return [x1Coord, y1Coord];
    },

    setActiveObject(designator) {
        const spaceObjects = window["vt"].spaceObjects;
        const isPerturbed = SynodicCalculationService.getPerturbedSynodicOrbitList().includes(designator);
        const spaceObjectId = SynodicCalculationService.getTransformedSpaceObjectId(designator, isPerturbed);
        const spaceObject = UtilsService.findItemInObjectList("_id", spaceObjectId, spaceObjects);
        SynodicCalculationService.setSynodicActiveObject(spaceObject);
    },

    createSynodicOrbitObject(ephemData, objectData, spaceObjectData, designator) {
        const earthObject = SynodicCalculationService.getEarthObjectData();
        const { startDateJulian, endDateJulian } =
            SynodicCalculationService.getCurrentJulianTimeframe();
        const { absoluteMagnitude, slopeParameter } = objectData;

        const transformedOrbitPointsData = [];

        for (
            let dayJulian = startDateJulian;
            dayJulian <= endDateJulian;
            dayJulian += SynodicCalculationService.PROPAGATION_TIME_STEP_DAY
        ) {
            transformedOrbitPointsData.push({
                ...SynodicCalculationService.calculateSynodicOrbitPointData(
                    dayJulian,
                    earthObject,
                    spaceObjectData,
                    absoluteMagnitude,
                    slopeParameter,
                    ephemData           // pass orbital elements in
                ),
                epoch: dayJulian,
                date: UtilsService.julianToDatestring(dayJulian),
            });
        }

        SynodicChartService.updateSynodicOrbitObjectChartsData(
            designator,
            startDateJulian,
            endDateJulian,
            transformedOrbitPointsData.map(
                // eslint-disable-next-line
                ({ epoch, xa, ya, za, ...pointChartData }) => pointChartData
            )
        );

        SynodicCalculationService.addSynodicOrbit(
            transformedOrbitPointsData,
            ephemData,
            designator
        );
    },

    calculateSynodicOrbitPointData(
        dayJulian,
        earthObject,
        spaceObjectData,
        absoluteMagnitude,
        slopeParameter,
        ephemData
    ) {
        // ---------------------------------------------------------
        // 1. Get Earth heliocentric position at this time
        // ---------------------------------------------------------
        //console.log("EPHEM DEBUG", ephemData);
        const earthNormalPosition = earthObject.getPosition(dayJulian);
        const [xNormalEarth, yNormalEarth] = earthNormalPosition;

        const {
            x: xTransformedEarth,
            y: yTransformedEarth,
            z: zTransformedEarth,
        } = SynodicCalculationService.calculateTransfomredEarthPointData(
            earthObject,
            dayJulian
            );

        // ---------------------------------------------------------
        // 2. Get asteroid heliocentric position
        //
        //    For elliptical (e <= 1): use Spacekit's getPosition.
        //    For hyperbolic (e > 1): use our own two-body hyperbolic solver
        //    computeHyperbolicKeplerPosition to avoid Spacekit issues.
        // ---------------------------------------------------------
        const ecc = Number(ephemData && (ephemData.e || ephemData.eccentricity || ephemData.ecc));

        let xNormalObject;
        let yNormalObject;
        let zNormalObject;

        if (Number.isFinite(ecc) && ecc >= 1) {
            const hyperPos = SynodicCalculationService.computeHyperbolicKeplerPosition(
                dayJulian,
                ephemData
            );
            //console.log("[HYP POS TEST]", dayJulian, ephemData.designator, hyperPos);

            if (hyperPos) {
                [xNormalObject, yNormalObject, zNormalObject] = hyperPos;
            } else {
                // Fallback to Spacekit if something went wrong
                [xNormalObject, yNormalObject, zNormalObject] =
                spaceObjectData.getPosition(dayJulian);
            }
        } else {
            // Elliptical / parabolic: original behaviour
            [xNormalObject, yNormalObject, zNormalObject] =
                spaceObjectData.getPosition(dayJulian);
        }

        // ---------------------------------------------------------
        // 3. Synodic transformation
        // ---------------------------------------------------------
        const dx = xNormalObject - xNormalEarth;
        const dy = yNormalObject - yNormalEarth;

        const modulo = +VisualisationService.distanceToCenter(earthNormalPosition, 10);
        const u1x = xNormalEarth / modulo;
        const u1y = yNormalEarth / modulo;
        const u2x = -xTransformedEarth * u1y;
        const u2y = u1x;

        const xTransformedObject = dx * u1x + dy * u1y + xTransformedEarth;
        const yTransformedObject = dx * u2x + dy * u2y;

        const transformedSpaceObjectSunDistance = +VisualisationService.distanceToCenter(
            [xTransformedObject, yTransformedObject, zNormalObject],
            10
        );
        const transformedSpaceObjectTransformedEarthDistance = +VisualisationService.distanceToBody(
            [xTransformedObject, yTransformedObject, zNormalObject],
            [xTransformedEarth, yTransformedEarth, zTransformedEarth],
            10
        );

        let argument =
            (Math.pow(transformedSpaceObjectSunDistance, 2) +
                Math.pow(transformedSpaceObjectTransformedEarthDistance, 2) -
                Math.pow(xTransformedEarth, 2)) /
            (2 * transformedSpaceObjectSunDistance * transformedSpaceObjectTransformedEarthDistance);

        // numeric safety to avoid NaN in acos
        argument = Math.max(-1, Math.min(1, argument));

        const transformedSpaceObjectPhaseAngle = Math.acos(argument) * (180 / Math.PI);

        const transformedSpaceObjectPhaseAngleTangens = Math.tan((transformedSpaceObjectPhaseAngle * Math.PI) / 180 / 2 );

        const transformedSpaceObjectPhi1 = Math.exp(-3.33 * Math.pow(transformedSpaceObjectPhaseAngleTangens, 0.63));
        const transformedSpaceObjectPhi2 = Math.exp(-1.87 * Math.pow(transformedSpaceObjectPhaseAngleTangens, 1.22));
        const apparentMag =
            absoluteMagnitude -
            2.5 *
                Math.log10(
                    (1 - slopeParameter) * transformedSpaceObjectPhi1 + slopeParameter * transformedSpaceObjectPhi2
                ) +
            5 * Math.log10(transformedSpaceObjectSunDistance * transformedSpaceObjectTransformedEarthDistance);
        const elongationAngle = Math.atan2(yTransformedObject, xTransformedEarth - xTransformedObject) * (180 / Math.PI);

        return {
            xa: xTransformedObject,
            ya: yTransformedObject,
            za: zNormalObject,
            earthDistance: transformedSpaceObjectTransformedEarthDistance,
            phaseAngle: transformedSpaceObjectPhaseAngle,
            elongationAngle: isNaN(elongationAngle) ? null : elongationAngle,
            apparentMag,
        };
    },

    computeHyperbolicKeplerPosition(jd, ephemData) {
        // --- 1. Read and sanitise orbital elements ---
        const q0 = Number(ephemData.q);
        let   e  = Number(ephemData.e || ephemData.eccentricity || ephemData.ecc);
        const i  = Number(ephemData.i);    // degrees
        const om = Number(ephemData.om);   // Ω, degrees
        const w  = Number(ephemData.w);    // ω, degrees
        let  tp  = Number(ephemData.tp);   // MJD or JD

        // Handle the parabolic edge case: if e == 1, nudge into hyperbolic regime.
        // Do NOT change elliptical orbits (e < 1).
        const PARABOLIC_EPS = 1e-6;
        if (Number.isFinite(e) && Math.abs(e - 1) < 1e-12) {
            e = 1 + PARABOLIC_EPS;
        }

        // Basic validation AFTER the adjustment
        if (!Number.isFinite(q0) || !Number.isFinite(e) || e <= 1 ||
            !Number.isFinite(i)   || !Number.isFinite(om) ||
            !Number.isFinite(w)   || !Number.isFinite(tp) ||
            !Number.isFinite(jd)) {
            return null;
        }

        // --- 2. Put jd and tp on the SAME time scale (JD) ---

        let t  = Number(jd);  // time of interest
        let tpFull = tp;

        const MJD_JD_OFFSET = 2400000.5;

        const isMJD = (v) => v < 1_000_000;
        const isJD  = (v) => v > 2_300_000;

        const tIsMJD  = isMJD(t);
        const tIsJD   = isJD(t);
        const tpIsMJD = isMJD(tpFull);
        const tpIsJD  = isJD(tpFull);

        // Case 1: both look like MJD → convert both to JD
        if (tIsMJD && tpIsMJD) {
            t      = t      + MJD_JD_OFFSET;
            tpFull = tpFull + MJD_JD_OFFSET;
        }
        // Case 2: time is JD, tp is MJD -> convert tp to JD
        else if (tIsJD && tpIsMJD) {
            tpFull = tpFull + MJD_JD_OFFSET;
        }
        // Case 3: time is MJD, tp is JD -> convert t to JD
        else if (tIsMJD && tpIsJD) {
            t = t + MJD_JD_OFFSET;
        }
        // If both already look like JD, leave them as they are.

        // --- 3. Hyperbolic Keplerian motion in orbital plane ---

        const q = q0;                  // perihelion distance [AU]
        const rad = Math.PI / 180;
        const inc   = i  * rad;
        const Omega = om * rad;
        const omega = w  * rad;

        // Semi-major axis for hyperbola: a = q / (e - 1)  (positive)
        const a = q / (e - 1);

        // Solar GM in AU^3/day^2
        const mu = 0.00029591220828559104;

        // Mean motion magnitude |n| = sqrt(mu / |a|^3)
        const n = Math.sqrt(mu / Math.abs(a * a * a));

        // Hyperbolic mean anomaly: M = e sinh(H) - H
        const M = n * (t - tpFull);

        // Initial guess for H (hyperbolic eccentric anomaly)
        let H = Math.log(2 * Math.abs(M) / e + 1);
        if (M < 0) {
            H = -H;
        }

        // Newton iterations to solve e * sinh(H) - H = M
        for (let iter = 0; iter < 30; iter++) {
            const s  = Math.sinh(H);
            const c  = Math.cosh(H);
            const f  = e * s - H - M;
            const fp = e * c - 1;
            const dH = -f / fp;
            H += dH;
            if (Math.abs(dH) < 1e-12) {
                break;
            }
        }

        // True anomaly ν from H
        const k  = Math.sqrt((e + 1) / (e - 1));
        const nu = 2 * Math.atan(k * Math.tanh(H / 2));

        // Radius r
        const r = a * (e * Math.cosh(H) - 1);

        // Position in orbital (perifocal) plane
        const x_orb = r * Math.cos(nu);
        const y_orb = r * Math.sin(nu);

        // --- 4. Rotate from perifocal -> inertial (ICRF/ecliptic) ---

        const cosO = Math.cos(Omega);
        const sinO = Math.sin(Omega);
        const cosi = Math.cos(inc);
        const sini = Math.sin(inc);
        const cosw = Math.cos(omega);
        const sinw = Math.sin(omega);

        const x =
            x_orb * (cosO * cosw - sinO * sinw * cosi) -
            y_orb * (cosO * sinw + sinO * cosw * cosi);

        const y =
            x_orb * (sinO * cosw + cosO * sinw * cosi) -
            y_orb * (sinO * sinw - cosO * cosw * cosi);

        const z =
            x_orb * (sini * sinw) +
            y_orb * (sini * cosw);

        return [x, y, z];
    },

    addSynodicOrbit(transformedOrbitPointsData, ephemData, designator, isPerturbed = false) {
        const vt = window["vt"];
        const objectsSettings = VisualisationService.getSettings().objectsSettings;
        // const isEarthOnScale = VisualisationService.getSettings().objectsSettings.earthOnScale.value;
        // const asteroidScaleFactor = isEarthOnScale ? SynodicCalculationService.EARTH_ON_SCALE_ASTEROID_FACTOR : 1;
        const diam = ephemData.diam ? ephemData.diam : UtilsService.estimateDiameter(ephemData.mag);
        const { synodicOrbitPoints, synodicOrbitYears } = SynodicCalculationService.getSynodicOrbitPointsInfo();
        const ephemTransformedSpaceObject =
            SynodicCalculationService.createEphemeridesTable(transformedOrbitPointsData);

        const transformedSpaceObjectOptions = {
            ...SpacekitService.spaceObjectOptions(),
            ...{
                name: ephemData.objectName || ephemData.designator,
                diam,
                mag: ephemData.mag,
                labelLayer: LayerService.getToolLayers().OBJECT_NAMES,
                ephemTable: ephemTransformedSpaceObject,
                orbitPathSettings: {
                    numberSamplePoints: 25 * synodicOrbitPoints,
                    leadDurationYears: synodicOrbitYears,
                    trailDurationYears: synodicOrbitYears,
                },
            },
        };
        const spaceObjectId = SynodicCalculationService.getTransformedSpaceObjectId(
            designator,
            isPerturbed
        );
        const transformedSpaceObject = vt.createSphere(
            spaceObjectId,
            transformedSpaceObjectOptions
        );
        transformedSpaceObject._object3js.name = designator.toString();
        // transformedSpaceObject._object3js.scale.setScalar(asteroidScaleFactor);
        let isObjectInvisible = false;
        const earthObject = vt.planets.find(({ _id }) => _id === SynodicCalculationService.TRANSFORMED_EARTH_OBJECT_NAME);
        const impactDate = ephemData.objectName ? SpacekitService.checkForSyntheticObjectImpact(transformedSpaceObject, earthObject, ephemTransformedSpaceObject.data[ephemTransformedSpaceObject.data.length - 1][0]) : SynodicCalculationService.checkImpactDate(designator);
        transformedSpaceObject.impactDate = impactDate;
        
        if (isPerturbed && impactDate) {
            const simulationDate = moment.utc(TimeFrameService.getSimulationTime()).format("YYYY-MM-DD");
            const simulationTimestamp = new Date(simulationDate).getTime();
            const impactTimestamp = new Date(impactDate).getTime();
            isObjectInvisible = simulationTimestamp > impactTimestamp;
        }

        isObjectInvisible && (transformedSpaceObject._object3js.visible = false);
        vt.spaceObjects.push(transformedSpaceObject);
        SpacekitService.addObjectMarker(transformedSpaceObject);
        SpacekitService.handleSynodicOrbitHiglight();
        !isObjectInvisible &&
            HUDService.createLabel(
                transformedSpaceObject,
                UtilsService.hexColor(objectsSettings.objectNames.color),
                objectsSettings.objectNamesSize.value
            );

        if (designator === VisualisationService.getSelectedSynodicObjectName()) {
            SynodicCalculationService.setSynodicActiveObject(transformedSpaceObject);
        }

        if (isPerturbed && impactDate) {
            PopupService.show({
                component: "PopupInfo",
                type: "warning",
                message: Keys.t.trajectoryImpact
                    .replace("{objectName}", designator)
                    .replace("{impactDate}", impactDate),
            });
        }

        const settings = VisualisationService.getSettings();
        const asteroidScaleFactor = settings && settings.objectsSettings.asteroidsSizeFactor.value;
        SpacekitService.updateAsteroidsSizeFactor(asteroidScaleFactor, 'sovt');
        SpacekitService.toggleLightEffect(false);
    },

    async createSynodicPerturbedOrbitObject(ephemData, objectData, designator) {
        const { startDateJulian, endDateJulian } = SynodicCalculationService.getCurrentJulianTimeframe();
        const { absoluteMagnitude, slopeParameter } = objectData;

        const synodicPerturbedData = SynodicCalculationService.getSynodicPerturbedData();
        let perturbedPointsData; 
        let isObjectFound = synodicPerturbedData.some(obj => {
            return obj.designator === designator;
        });

        if (!isObjectFound || SynodicCalculationService.isDateChanged(designator)){
            perturbedPointsData = await SynodicCalculationService.getPerturbedSynodicOrbitParameters(designator, ephemData.type ? ephemData.type : (objectData.type ? objectData.type : null));
        }
        else {
            let foundObject = synodicPerturbedData.find(obj => {
                return obj.designator === designator;
            });
            perturbedPointsData = foundObject.data;
        }

        const hasPoints = SynodicCalculationService.hasPerturbedPoints(perturbedPointsData);
        if (!hasPoints) {
            return false;
        }
        const transformedOrbitPointsData = SynodicCalculationService.getTransformedPerturbedPointsData(
            perturbedPointsData,
            absoluteMagnitude,
            slopeParameter,
            startDateJulian
        );
        SynodicChartService.updateSynodicOrbitObjectChartsData(
            designator,
            startDateJulian,
            endDateJulian,
            transformedOrbitPointsData.map(
                // eslint-disable-next-line
                ({ epoch, xa, ya, za, ...pointChartData }) => pointChartData
            ),
            true
        );

        const synodicperturbedObject = {};
        const maximumObjectNumber = VisualisationService.getSettings().maxSavedPerturbedObjects;
        synodicperturbedObject.designator = designator;
        synodicperturbedObject.data = perturbedPointsData;


        if (!isObjectFound)
            synodicPerturbedData.push(synodicperturbedObject);
        else if (isObjectFound && SynodicCalculationService.isDateChanged(designator)){
            let foundObject = synodicPerturbedData.find(obj => {
                return obj.designator === designator;
            });
            foundObject.data = perturbedPointsData;
        }

        if (synodicPerturbedData.length > maximumObjectNumber)
            synodicPerturbedData.shift();

        SynodicCalculationService.setSynodicPerturbedData(synodicPerturbedData);


        SynodicCalculationService.addSynodicOrbit(transformedOrbitPointsData, ephemData, designator, true);
        return true;

    },

    getSynodicOrbitPointsInfo() {
        const { startDateJulian, endDateJulian } = SynodicCalculationService.getCurrentJulianTimeframe();
        const synodicOrbitPoints =
            endDateJulian - startDateJulian / SynodicCalculationService.PROPAGATION_TIME_STEP_DAY;
        const synodicOrbitYears = (endDateJulian - startDateJulian) / SynodicCalculationService.EARTH_PERIOD;
        return { synodicOrbitPoints, synodicOrbitYears };
    },

    recalculateVisibleSynodicOrbits() {
        const visibleRealSynodicObjectsList = UtilsService.deepCopy(
            SynodicCalculationService.getVisibleRealSynodicObjectsList()
        );
        if (!visibleRealSynodicObjectsList.length) {
            return;
        }
        const perturbedOrbitList = SynodicCalculationService.getPerturbedSynodicOrbitList();
        const asteroidsList = ObjectsService.getFinalAsteroidsList();
        const syntheticObjectsList = SyntheticObjectsService.getSyntheticObjectsList();
        const removedObjectsList = [];
        visibleRealSynodicObjectsList.forEach(async (objectDesignator) => {
            const isPerturbedOrbit = perturbedOrbitList.includes(objectDesignator);
            SynodicCalculationService.removeExistingSpaceObject(objectDesignator, isPerturbedOrbit);
            if (
                !asteroidsList.some(
                    (asteroid) => (asteroid.number ? asteroid.number : asteroid.name).toString() === objectDesignator
                ) &&
                !syntheticObjectsList.some((object) => object.designator === objectDesignator)
            ) {
                removedObjectsList.push(objectDesignator);
                return;
            }
            const { objectParameters, objectEphemData } =
                SynodicCalculationService.getCalculatedInvisibleObjectParameters(objectDesignator);
            const spaceObjectOrbitData = SpacekitService.getSpaceObjectData(objectEphemData);
            if (!spaceObjectOrbitData) {
                return;
            }
            const isDetectionPolarObjectAdded = SpacekitService.getDetectionPolarObjects().length !== 0;
            const {
                isObjectSelected,
            } = SynodicCalculationService.getSynodicObjectVisibilityInfo(
                objectDesignator
            );
            if (!isDetectionPolarObjectAdded && isObjectSelected) {
                SynodicCalculationService.createOrbitRange(
                    objectParameters,
                    spaceObjectOrbitData
                );
                SynodicCalculationService.createDetectionPolarObjects(objectParameters);
            }
            !isPerturbedOrbit &&
                SynodicCalculationService.createSynodicOrbitObject(
                    objectEphemData,
                    objectParameters,
                    spaceObjectOrbitData,
                    objectDesignator
                );
            if (isPerturbedOrbit) {
                const isPerturbedOrbitAvailable = await SynodicCalculationService.createSynodicPerturbedOrbitObject(
                    objectEphemData,
                    objectParameters,
                    objectDesignator
                );
                !isPerturbedOrbitAvailable && SynodicCalculationService.handlePerturbedUnavailable(objectDesignator);
            }
            SynodicCalculationService.checkIsFocusOnObject(objectDesignator);
        });
        if (!removedObjectsList.length) {
            return;
        }
        const calculatedObjectList = UtilsService.deepCopy(
            SynodicCalculationService.getCalculatedRealSynodicObjectsList()
        );
        removedObjectsList.forEach((objectDesignator) => {
            const calculatedObjectIndex = UtilsService.findItemIndexInObjectList(
                "designator",
                objectDesignator,
                calculatedObjectList
            );
            calculatedObjectIndex !== -1 && calculatedObjectList.splice(calculatedObjectIndex, 1);
            SynodicCalculationService.removePerturbedObjectOrbit(objectDesignator);
            const { isObjectSelected } = SynodicCalculationService.getSynodicObjectVisibilityInfo(objectDesignator);
            isObjectSelected && SynodicCalculationService.deselectVisibleSpaceObject();
            SynodicChartService.removeSynodicOrbitObjectChartData(objectDesignator);
        });
        SynodicCalculationService.setCalculatedRealSynodicObjectsList(calculatedObjectList);
        const filteredVisibleRealSynodicObjectsList = visibleRealSynodicObjectsList.filter(
            (designator) => !removedObjectsList.includes(designator)
        );
        SynodicCalculationService.setVisibleRealSynodicObjectsList(filteredVisibleRealSynodicObjectsList);
    },

    handlePerturbedUnavailable(designator) {
        SynodicCalculationService.showNoPerturbedInfo(designator);
        SynodicChartService.removeSynodicOrbitObjectChartData(designator, false);
        const perturbedOrbitList = UtilsService.deepCopy(SynodicCalculationService.getPerturbedSynodicOrbitList());
        const objectIndex = perturbedOrbitList.indexOf(designator);
        objectIndex !== -1 ? perturbedOrbitList.splice(objectIndex, 1) : null;
        SynodicCalculationService.setPerturbedSynodicOrbitList(
            perturbedOrbitList
        );
        const {
            objectParameters,
            objectEphemData,
        } = SynodicCalculationService.getCalculatedInvisibleObjectParameters(
            designator
        );
        const spaceObjectOrbitData = SpacekitService.getSpaceObjectData(
            objectEphemData
        );
        if (!spaceObjectOrbitData) {
            return;
        }
        SynodicCalculationService.createSynodicOrbitObject(
            objectEphemData,
            objectParameters,
            spaceObjectOrbitData,
            designator
        );
    },

    getTransformedSpaceObjectId(designator, isPerturbed = false) {
        return `${designator}${
            isPerturbed
                ? SynodicCalculationService.TRANSFORMED_SPACE_OBJECT_SUFFIX_PERTURBED
                : SynodicCalculationService.TRANSFORMED_SPACE_OBJECT_SUFFIX
        }`;
    },

    handleObjectVisibility(designator) {
        const { isObjectVisible } = SynodicCalculationService.getSynodicObjectVisibilityInfo(designator);
        isObjectVisible
            ? SynodicCalculationService.removeVisibleCalculatedObject(designator)
            : SynodicCalculationService.showCalculatedInvisibleObject(designator);
    },

    removeVisibleCalculatedObject(designator) {
        const isFocusedDeselected = SynodicCalculationService.isObjectFocused(designator);
        isFocusedDeselected && VisualisationService.setSovtFocusedObject('');
        const { isObjectSelected, objectIndexInVisibleList } =
            SynodicCalculationService.getSynodicObjectVisibilityInfo(designator);
        const visibleRealSynodicObjectsList = SynodicCalculationService.getVisibleRealSynodicObjectsList();
        if (objectIndexInVisibleList !== -1) {
            visibleRealSynodicObjectsList.splice(objectIndexInVisibleList, 1);
            SynodicCalculationService.setVisibleRealSynodicObjectsList(visibleRealSynodicObjectsList);
        }
        SynodicCalculationService.removeAllExistingSpaceObjects(designator);
        SynodicChartService.removeSynodicOrbitObjectChartData(designator);
        isObjectSelected && SynodicCalculationService.deselectVisibleSpaceObject();
    },

    removePerturbedObjectOrbit(designator) {
        const perturbedOrbitsList = SynodicCalculationService.getPerturbedSynodicOrbitList();
        const objectIndex = perturbedOrbitsList.indexOf(designator);
        if (objectIndex === -1) {
            return;
        }
        perturbedOrbitsList.splice(objectIndex, 1);
        SynodicCalculationService.setPerturbedSynodicOrbitList(perturbedOrbitsList);
    },

    async showCalculatedInvisibleObject(designator) {
        const visibleRealSynodicObjectsList = SynodicCalculationService.getVisibleRealSynodicObjectsList();
        const calculatedObject = await SynodicCalculationService.ensureCalculatedSynodicObject(designator);
        if (!calculatedObject) {
            PopupService.show({
                component: "PopupInfo",
                type: "warning",
                message: Keys.t.missingParameters,
            });
            return;
        }
        const { objectParameters, objectEphemData } =
            SynodicCalculationService.getCalculatedInvisibleObjectParameters(designator);
        const isOrbitAvailable = await SynodicCalculationService.createSynodicOrbit(
            objectParameters,
            objectEphemData,
            designator
        );
        if (!isOrbitAvailable) {
            SynodicCalculationService.handlePerturbedUnavailable(designator);
            return;
        }
        if (visibleRealSynodicObjectsList.includes(designator)) {
            return;
        }
        visibleRealSynodicObjectsList.push(designator);
        await SynodicCalculationService.setVisibleRealSynodicObjectsList(visibleRealSynodicObjectsList);
    },

    async ensureCalculatedSynodicObject(designator) {
        const existingCalculatedObject = UtilsService.findItemInObjectList(
            "designator",
            designator,
            SynodicCalculationService.getCalculatedRealSynodicObjectsList()
        );
        if (existingCalculatedObject) {
            return existingCalculatedObject;
        }

        const objectData = await SynodicCalculationService.getSynodicObjectDataByDesignator(designator);
        if (!objectData) {
            return null;
        }

        const parametersMap = SynodicCalculationService.getObjectParametersMap(objectData.type);
        const objectParameters = {};
        Object.keys(parametersMap).forEach((key) => (objectParameters[parametersMap[key]] = objectData[key]));
        const magnitudeLimit = 18;
        objectParameters["magnitudeLimit"] = magnitudeLimit;
        // magnitudeLimit is configurable later; the rest must exist to build an orbit.
        // eslint-disable-next-line no-unused-vars
        const { magnitudeLimit: ignoredMagnitudeLimit, ...requiredParameters } = objectParameters;
        if (!UtilsService.checkObjectPropertiesHasValue(requiredParameters)) {
            return null;
        }

        const calculatedObject = {
            designator,
            objectData,
            magnitudeLimit,
        };
        const calculatedObjects = UtilsService.deepCopy(
            SynodicCalculationService.getCalculatedRealSynodicObjectsList()
        );
        calculatedObjects.push(calculatedObject);
        await SynodicCalculationService.setCalculatedRealSynodicObjectsList(calculatedObjects);
        return calculatedObject;
    },

    async getSynodicObjectDataByDesignator(designator) {
        const isSyntheticObject = SyntheticObjectsService.isSyntheticObject(designator);
        if (isSyntheticObject) {
            const syntheticObjectDataRaw = SyntheticObjectsService.getSyntheticObjectDataByDesignator(designator);
            if (!syntheticObjectDataRaw) {
                return null;
            }
            const objectType = syntheticObjectDataRaw.type ||
                (SyntheticObjectsService.isSyntheticObject(designator, SyntheticObjectsService.OBJECT_TYPES.hyperbolic)
                    ? SyntheticObjectsService.OBJECT_TYPES.hyperbolic
                    : SyntheticObjectsService.OBJECT_TYPES.elliptical);
            const syntheticObjectData = UtilsService.deepCopy(
                SyntheticObjectsService.getSyntheticObjectParameters(designator, objectType)
            );
            if (!syntheticObjectData) {
                return null;
            }
            syntheticObjectData.catalogueNumber = syntheticObjectData.designator;
            syntheticObjectData.type = objectType;
            SynodicCalculationService.applyDefaultObjectParameters(syntheticObjectData);
            return syntheticObjectData;
        }

        const apiUrl = SynodicCalculationService.getEphemeridesUrl(designator);
        const loadingObjects = SynodicCalculationService.loadingObjectsEphemerides();
        if (!loadingObjects.includes(designator)) {
            loadingObjects.push(designator);
        }
        try {
            const response = await axios(apiUrl);
            const objectInformation = response.data && response.data.ephemerides ? response.data.ephemerides : null;
            if (!objectInformation) {
                SynodicCalculationService.showNoEphemeridesData(designator);
                return null;
            }
            const errorObject = objectInformation.errors && objectInformation.errors.length ? objectInformation.errors[0] : null;
            if (errorObject) {
                SynodicCalculationService.showNoEphemeridesData(designator);
                return null;
            }
            const data = objectInformation.data;
            const objectParameters = data && data.length ? data[0] : null;
            if (!objectParameters) {
                SynodicCalculationService.showNoEphemeridesData(designator);
                return null;
            }
            objectParameters.catalogueNumber = objectParameters.catalogueNumber ?? objectParameters.designator;
            SynodicCalculationService.applyDefaultObjectParameters(objectParameters);
            return objectParameters;
        } catch (error) {
            SynodicCalculationService.showNoEphemeridesData(designator);
            const message = UtilsService.prepareErrorMessage(error);
            console.warn(message);
            return null;
        } finally {
            const objectIndex = loadingObjects.indexOf(designator);
            objectIndex !== -1 && loadingObjects.splice(objectIndex, 1);
        }
    },

    applyDefaultObjectParameters(objectParameters) {
        !objectParameters.g && (objectParameters.g = 0.25);
        if (!objectParameters.aphelion && objectParameters.a && objectParameters.e) {
            objectParameters.aphelion = objectParameters.a * (1 + objectParameters.e);
        }
    },

    getCalculatedInvisibleObjectParameters(designator) {
        const invisibleCalculatedObject = UtilsService.findItemInObjectList(
            "designator",
            designator,
            SynodicCalculationService.getCalculatedRealSynodicObjectsList()
        );
        const objectParameters = {};
        const objectEphemData = invisibleCalculatedObject.objectData;
        const parametersMap = SynodicCalculationService.getObjectParametersMap(objectEphemData.type);
        Object.keys(parametersMap).forEach((key) => (objectParameters[parametersMap[key]] = objectEphemData[key]));
        objectParameters["magnitudeLimit"] = invisibleCalculatedObject.magnitudeLimit;
        return { objectParameters, objectEphemData };
    },

    switchOrbitToKeplerian(designator) {
        SynodicCalculationService.removeExistingSpaceObject(designator, true);
        const {
            objectParameters,
            objectEphemData,
        } = SynodicCalculationService.getCalculatedInvisibleObjectParameters(
            designator
        );
        const spaceObjectOrbitData = SpacekitService.getSpaceObjectData(
            objectEphemData
        );
        if (!spaceObjectOrbitData) {
            return;
        }
        SynodicCalculationService.createSynodicOrbitObject(
            objectEphemData,
            objectParameters,
            spaceObjectOrbitData,
            designator
        );
    },

    async switchOrbitToPerturbed(designator, objectType = null) {
        const synodicPerturbedData = SynodicCalculationService.getSynodicPerturbedData();
        let perturbedPointsData; 
        let isObjectFound = synodicPerturbedData.some(obj => {
            return obj.designator === designator;
        });
        
        if (!isObjectFound || SynodicCalculationService.isDateChanged(designator)){
            perturbedPointsData = await SynodicCalculationService.getPerturbedSynodicOrbitParameters(designator, objectType);
        }
        else {
            let foundObject = synodicPerturbedData.find(obj => {
                return obj.designator === designator;
            });
            perturbedPointsData = foundObject.data;
        }

        const hasPoints = SynodicCalculationService.hasPerturbedPoints(perturbedPointsData);
        if (!hasPoints) {
            SynodicCalculationService.handlePerturbedUnavailable(designator);
            return false;
        }
        SynodicCalculationService.removeExistingSpaceObject(designator,true);
        SynodicCalculationService.removeExistingSpaceObject(designator);

        const synodicperturbedObject = {};
        const maximumObjectNumber = VisualisationService.getSettings().maxSavedPerturbedObjects;
        synodicperturbedObject.designator = designator;
        synodicperturbedObject.data = perturbedPointsData;

        if (!isObjectFound)
            synodicPerturbedData.push(synodicperturbedObject);
        else if (isObjectFound && SynodicCalculationService.isDateChanged(designator)){
            let foundObject = synodicPerturbedData.find(obj => {
                return obj.designator === designator;
            });
            foundObject.data = perturbedPointsData;
        }

        if (synodicPerturbedData.length > maximumObjectNumber)
            synodicPerturbedData.shift();

        SynodicCalculationService.setSynodicPerturbedData(synodicPerturbedData);
        SynodicCalculationService.createSynodicPerturbedOrbit(designator, perturbedPointsData);
        return true;
    },

    createSynodicPerturbedOrbit(designator, perturbedPointsData) {
        const { startDateJulian, endDateJulian } = SynodicCalculationService.getCurrentJulianTimeframe();
        const { objectParameters, objectEphemData } =
            SynodicCalculationService.getCalculatedInvisibleObjectParameters(designator);
        const { absoluteMagnitude, slopeParameter } = objectParameters;
        const transformedOrbitPointsData = SynodicCalculationService.getTransformedPerturbedPointsData(
            perturbedPointsData,
            absoluteMagnitude,
            slopeParameter,
            startDateJulian
        );
        SynodicChartService.updateSynodicOrbitObjectChartsData(
            designator,
            startDateJulian,
            endDateJulian,
            transformedOrbitPointsData.map(
                // eslint-disable-next-line
                ({ epoch, xa, ya, za, ...pointChartData }) => pointChartData
            ),
            true
        );
        SynodicCalculationService.addSynodicOrbit(transformedOrbitPointsData, objectEphemData, designator, true);
    },

    getTransformedPerturbedPointsData(perturbedPointsData, absoluteMagnitude, slopeParameter, startDateJulian) {
        const positionData = Object.values(perturbedPointsData);
        const positionKeys = Object.keys(perturbedPointsData);
        const pointsData = positionData.map((positionInfo, index) => {
            const { asteroid: asteroidPositionInfo, earth: earthPositionInfo } = positionInfo;
            const asteroidPosition = [asteroidPositionInfo[0], asteroidPositionInfo[1], asteroidPositionInfo[2]];
            const earthPosition = [earthPositionInfo[0], earthPositionInfo[1], earthPositionInfo[2]];
            const epochDate = index === positionData.length - 1 ? positionKeys[index]*1 + 2400000.5 : startDateJulian + index;
            return {
                xa: asteroidPosition[0],
                ya: asteroidPosition[1],
                za: asteroidPosition[2],
                epoch: epochDate,
                date: UtilsService.julianToDatestring(epochDate),
                orbitalPeriod: positionInfo.orbital_period
                    ? parseFloat(positionInfo.orbital_period)
                    : null, //Math.random().toFixed(4), // mock value between 0 and 1 as a string, fixed to 4 decimals
                    ...SynodicCalculationService.calculateSynodicPerturbedOrbitPointData(
                    asteroidPosition,
                    earthPosition,
                    absoluteMagnitude,
                    slopeParameter
                ),
            };
        });

        if (pointsData[pointsData.length - 1].date === pointsData[pointsData.length - 2].date) {
            pointsData.splice(pointsData.length - 2, 1);
        }

        return pointsData;
    },

    removeExistingSpaceObject(designator, isPerturbed = false) {
        const vt = window["vt"];
        if (!vt || !vt.spaceObjects) {
            return;
        }

        // Always clear the marker once (regardless of how many duplicates we find)
        SpacekitService.removeSovtObjectMarkers(designator);

        const spaceObjectId = SynodicCalculationService.getTransformedSpaceObjectId(
            designator,
            isPerturbed
        );

        // If, for any reason, there are multiple transformed objects
        // with the same id, remove all of them.
        // (In the normal flow there should be at most one.)
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const spaceObject = UtilsService.findItemInObjectList(
                "_id",
                spaceObjectId,
                vt.spaceObjects
            );

            if (!spaceObject) {
                break;
            }

            HUDService.removeLabel(spaceObject);
            vt.removeObject(spaceObject);

            const indexToRemove = vt.spaceObjects.indexOf(spaceObject);
            if (indexToRemove !== -1) {
                vt.spaceObjects.splice(indexToRemove, 1);
            }
        }
    },

    removeAllExistingSpaceObjects(designator) {
        SynodicCalculationService.removeExistingSpaceObject(designator, false);
        SynodicCalculationService.removeExistingSpaceObject(designator, true);
    },

    calculateSynodicPerturbedOrbitPointData(objectPosition, earthPosition, absoluteMagnitude, slopeParameter) {
        const transformedSpaceObjectSunDistance = +VisualisationService.distanceToCenter(objectPosition, 10);
        const transformedSpaceObjectTransformedEarthDistance = +VisualisationService.distanceToBody(
            objectPosition,
            earthPosition,
            10
        );
        let argument =
            (Math.pow(transformedSpaceObjectSunDistance, 2) +
                Math.pow(transformedSpaceObjectTransformedEarthDistance, 2) -
                Math.pow(earthPosition[0], 2)) /
            (2 * transformedSpaceObjectSunDistance * transformedSpaceObjectTransformedEarthDistance);
        const transformedSpaceObjectPhaseAngle = Math.acos(argument) * (180 / Math.PI);
        const transformedSpaceObjectPhaseAngleTangens = Math.tan(
            (transformedSpaceObjectPhaseAngle * Math.PI) / 180 / 2
        );
        const transformedSpaceObjectPhi1 = Math.exp(-3.33 * Math.pow(transformedSpaceObjectPhaseAngleTangens, 0.63));
        const transformedSpaceObjectPhi2 = Math.exp(-1.87 * Math.pow(transformedSpaceObjectPhaseAngleTangens, 1.22));
        const apparentMag =
            absoluteMagnitude -
            2.5 *
                Math.log10(
                    (1 - slopeParameter) * transformedSpaceObjectPhi1 + slopeParameter * transformedSpaceObjectPhi2
                ) +
            5 * Math.log10(transformedSpaceObjectSunDistance * transformedSpaceObjectTransformedEarthDistance);
        const elongationAngle = Math.atan2(objectPosition[1], earthPosition[0] - objectPosition[0]) * (180 / Math.PI);
        return {
            earthDistance: transformedSpaceObjectTransformedEarthDistance,
            phaseAngle: transformedSpaceObjectPhaseAngle,
            elongationAngle: isNaN(elongationAngle) ? null : elongationAngle,
            apparentMag,
        };
    },

    deselectVisibleSpaceObject() {
        VisualisationService.setSelectedSynodicObjectName("");
        SpacekitService.removeDetectionPolarObjects();
        SpacekitService.removeOrbitRangeObject();
    },

    toggleSynodicPlanets(isVisible) {
        if (isVisible) {
            SynodicCalculationService.removeExistingTransformedPlanetsObject();
            SynodicCalculationService.createTransformedPlanets();
            return;
        }
        const focusedObject = VisualisationService.getSovtFocusedObject();
        const isEarthSelected = focusedObject === SynodicCalculationService.TRANSFORMED_EARTH_OBJECT_NAME;
        const isPlanetSelected = Config.planetsSelectList.map(planet => `${planet.toLowerCase()}${SynodicCalculationService.TRANSFORMED_SPACE_OBJECT_SUFFIX}`).some(planet => planet === focusedObject);
        !isEarthSelected && isPlanetSelected && VisualisationService.setSovtFocusedObject('');
        SynodicCalculationService.removeExistingTransformedPlanetsObject();
    },

    recalculateTransformedPlanets() {
        const settings = VisualisationService.getSettings();
        const isPlanetsVisible = settings && settings.objectsSettings.synodicPlanets.value;
        if (!isPlanetsVisible) {
            return;
        }
        SynodicCalculationService.removeExistingTransformedPlanetsObject();
        SynodicCalculationService.createTransformedPlanets();
    },

    createTransformedPlanets() {
        const earthObject = SynodicCalculationService.getEarthObjectData();
        const { startDateJulian, endDateJulian } = SynodicCalculationService.getCurrentJulianTimeframe();
        const { synodicOrbitPoints, synodicOrbitYears } = SynodicCalculationService.getSynodicOrbitPointsInfo();
        const orbitPathSettings = {
            numberSamplePoints: 2 * synodicOrbitPoints,
            leadDurationYears: synodicOrbitYears,
            trailDurationYears: synodicOrbitYears,
        };
        SynodicCalculationService.PLANETS_DEFAULT_OPTIONS.forEach((planetOptions, index) => {
            const planetName = SynodicCalculationService.TRANSFORMED_PLANETS_NAMES[index];
            const extendedPlanetOptions = {
                ...planetOptions,
                orbitPathSettings,
            };
            const planetData = SynodicCalculationService.getPlanetData(planetName, planetOptions);
            const transformedOrbitPointsData = [];
            for (
                let dayJulian = startDateJulian;
                dayJulian <= endDateJulian;
                dayJulian += SynodicCalculationService.PROPAGATION_TIME_STEP_DAY
            ) {
                transformedOrbitPointsData.push({
                    ...SynodicCalculationService.calculateSynodicOrbitplanetPointData(
                        dayJulian,
                        earthObject,
                        planetData
                    ),
                    epoch: dayJulian,
                });
            }
            const planetEphemeridesTable = SynodicCalculationService.createEphemeridesTable(transformedOrbitPointsData);
            SynodicCalculationService.addTransformedPlanetObject(
                planetName,
                extendedPlanetOptions,
                planetEphemeridesTable
            );
            SynodicCalculationService.checkIsFocusOnObject(planetName);
        });
        const objectsSettings = VisualisationService.getSettings().objectsSettings;
        const isPlanetLabelVisible = objectsSettings.synodicPlanetsNames.value;
        const isPlanetOrbitVisible = objectsSettings.synodicPlanetsOrbits.value;
        const orbitColor = objectsSettings.synodicPlanetsOrbits.color;
        SynodicCalculationService.toggleSynodicPlanetsNames(isPlanetLabelVisible);
        SynodicCalculationService.toggleSynodicPlanetsOrbits(isPlanetOrbitVisible);
        SynodicCalculationService.updateSynodicPlanetsOrbitsColor(orbitColor);
    },

    removeExistingTransformedPlanetsObject() {
        const vt = window["vt"];
        vt.planetMarkers = vt.planetMarkers || {};

        SynodicCalculationService.TRANSFORMED_PLANETS_NAMES.forEach((planetName) => {
            const transformedPlanetObject = UtilsService.findItemInObjectList("_id", planetName, vt.planets);
            if (transformedPlanetObject) {
                // remove label and planet object
                HUDService.removeLabel(transformedPlanetObject);
                vt.removeObject(transformedPlanetObject);
                const indexToRemove = vt.planets.indexOf(transformedPlanetObject);
                vt.planets.splice(indexToRemove, 1);
            }

            const marker = vt.planetMarkers[planetName];
            if (marker) {
                vt.getScene().remove(marker);
                marker.geometry?.dispose?.();
                marker.material?.dispose?.();
                delete vt.planetMarkers[planetName];
            }
        });
    },

    calculateSynodicOrbitplanetPointData(dayJulian, earthObject, planetData) {
        const earthNormalPosition = earthObject.getPosition(dayJulian);
        const [xNormalEarth, yNormalEarth] = earthNormalPosition;
        const [xNormalPlanet, yNormalPlanet, zNormalPlanet] = planetData.getPosition(dayJulian);
        const dx = xNormalPlanet - xNormalEarth;
        const dy = yNormalPlanet - yNormalEarth;
        const modulo = +VisualisationService.distanceToCenter(earthNormalPosition, 10);
        const u1x = xNormalEarth / modulo;
        const u1y = yNormalEarth / modulo;
        const u2x = -1 * u1y;
        const u2y = u1x;
        const xTransformedPlanet = dx * u1x + dy * u1y + 1;
        const yTransformedPlanet = dx * u2x + dy * u2y;
        return {
            xa: xTransformedPlanet,
            ya: yTransformedPlanet,
            za: zNormalPlanet,
        };
    },

    getPlanetData(planetName, planetOptions) {
        const vt = window["vt"];
        const planet = vt.createSphere(planetName, planetOptions);
        vt.removeObject(planet);
        return planet;
    },

    addTransformedPlanetObject(planetName, planetOptions, planetEphemeridesTable) {
        const vt = window["vt"];
        const transformedPlanetObjectOptions = {
            ...planetOptions,
            ephemTable: planetEphemeridesTable,
            labelLayer: LayerService.getToolLayers().PLANET_NAMES,
        };
        const transformedPlanetObject = vt.createSphere(planetName, transformedPlanetObjectOptions);
        transformedPlanetObject._object3js.name = planetName.toString();
        vt.planets.push(transformedPlanetObject);
        const objectsSettings = VisualisationService.getSettings().objectsSettings;
        HUDService.createLabel(
            transformedPlanetObject,
            UtilsService.hexColor(objectsSettings.synodicPlanetsNames.color),
            objectsSettings.synodicPlanetsNamesSize.value
        );

        vt.planetMarkers = vt.planetMarkers || {};
        if (vt.planetMarkers[planetName]) {
            const old = vt.planetMarkers[planetName];
            vt.getScene().remove(old);
            old.geometry?.dispose?.();
            old.material?.dispose?.();
            delete vt.planetMarkers[planetName];
        }
        // --- SOVT planet marker (tiny colored sphere) ---
        vt.planetMarkers = vt.planetMarkers || {};

        const PLANET_MARKER_COLORS = {
            mercury: 0xffea8e,
            venus:   0xf4ae25,
            earth:   0x1878bd,
            mars:    0xf44b25,
            jupiter: 0x9c2824,
            saturn:  0x9c6124,
            uranus:  0x3d37ad,
            neptune: 0x4ea7e7,
        };

        // planetName like "mars_transformed" → base "mars" for color
        const base = planetName.replace(SynodicCalculationService.TRANSFORMED_SPACE_OBJECT_SUFFIX, "");
        const color = PLANET_MARKER_COLORS[base] ?? 0xffffff;

        const geometry = new THREE.SphereGeometry(0.02, 16, 16);
        const material = new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity: 1.0,
            depthTest: false,
            depthWrite: false,
        });

        const marker = new THREE.Mesh(geometry, material);
        marker.frustumCulled = false;
        marker.renderOrder = 9999;
        marker.name = `marker_${planetName}`;

        // place at current position (tracking & scaling added in next step)
        const jdNow = vt.getJd ? vt.getJd() : UtilsService.dateToJulian(VisualisationService.getSimulationTime());
        const [x, y, z] = transformedPlanetObject.getPosition(jdNow);
        marker.position.set(x, y, z);

        vt.getScene().add(marker);
        vt.planetMarkers[planetName] = marker;
    },

    toggleSynodicPlanetsNames(isVisible) {
        const vt = window['vt'];
        isVisible ? vt.cameraOrtho.layers.enable(LayerService.getToolLayers().PLANET_NAMES) : vt.cameraOrtho.layers.disable(LayerService.getToolLayers().PLANET_NAMES);
    },

    updateSynodicPlanetLabel(labelOption, labelOptionValue) {
        const planets = window['vt'].planets;
        const excludedPlanetsList = SynodicCalculationService.getExludedSynodicPlanets();
        const filteredPlanets = planets.filter(planet => !excludedPlanetsList.includes(planet._id));
        filteredPlanets.forEach(planet => {
            if (!planet._label3d) {
                return;
            }
            labelOption === 'fontSize' && (planet._label3d[0].fontSize = labelOptionValue);
            labelOption === 'color' && (planet._label3d[0].color = labelOptionValue)
        });
    },

    toggleSynodicPlanetsOrbits(isVisible) {
        const planets = window['vt'].planets;
        const excludedPlanetsList = SynodicCalculationService.getExludedSynodicPlanets();
        const filteredPlanets = planets.filter(planet => !excludedPlanetsList.includes(planet._id));
        filteredPlanets.forEach(planet => planet.get3jsObjects()[2] && (planet.get3jsObjects()[2].visible = isVisible));
    },

    updateSynodicPlanetsOrbitsColor(color) {
        const planets = window['vt'].planets;
        const excludedPlanetsList = SynodicCalculationService.getExludedSynodicPlanets();
        const filteredPlanets = planets.filter(planet => !excludedPlanetsList.includes(planet._id));
        const orbitColor = new THREE.Color(UtilsService.hexColor(color));
        //filteredPlanets.forEach(planet => planet.getOrbit().setHexColor(orbitColor));
        filteredPlanets.forEach(planet => SpacekitService.setOrbitHexColor(planet, orbitColor));
    },

    getExludedSynodicPlanets() {
        return [SynodicCalculationService.TRANSFORMED_EARTH_OBJECT_NAME, SynodicCalculationService.EARTH_FAKE_MOON_NAME, SynodicCalculationService.EARTH_PLACEHOLDER_NAME];
    },

    createTransformedEarthObject() {
        SynodicCalculationService.removeExistingTransformedEarthObject();
        SynodicCalculationService.removeExistingEarthRotationHelpers();
        const earthObject = SynodicCalculationService.getEarthObjectData();
        const { startDateJulian, endDateJulian } = SynodicCalculationService.getCurrentJulianTimeframe();
        const transformedEarthPoints = [];
        for (
            let dayJulian = startDateJulian;
            dayJulian <= endDateJulian;
            dayJulian += SynodicCalculationService.PROPAGATION_TIME_STEP_DAY
        ) {
            const { x, y, z } = SynodicCalculationService.calculateTransfomredEarthPointData(earthObject, dayJulian);
            transformedEarthPoints.push({
                epoch: dayJulian,
                xa: x,
                ya: y,
                za: z,
            });
        }
        const earthEphemeridesTable = SynodicCalculationService.createEphemeridesTable(transformedEarthPoints);
        const fakeEarthEphemeridesTable = SynodicCalculationService.createEphemeridesTable(
            transformedEarthPoints.map((data) => ({ ...data, za: 0.5 }))
        );
        SynodicCalculationService.addTransformedEarthObject(earthEphemeridesTable, fakeEarthEphemeridesTable);
        SpacekitService.toggleLightEffect(false);
        SynodicCalculationService.checkIsFocusOnObject(SynodicCalculationService.TRANSFORMED_EARTH_OBJECT_NAME);
    },

    getEarthObjectData() {
        const vt = window["vt"];
        const earthObject = vt.createSphere("earth", SynodicCalculationService.EARTH_OBJECT_DEFAULT_OPTIONS);
        vt.removeObject(earthObject);
        return earthObject;
    },

    removeExistingTransformedEarthObject() {
        const vt = window["vt"];
        const earthId = SynodicCalculationService.TRANSFORMED_EARTH_OBJECT_NAME;
        const transformedEarthObject = UtilsService.findItemInObjectList(
            "_id",
            earthId,
            vt.planets
        );
        if (transformedEarthObject) {
            HUDService.removeLabel(transformedEarthObject);
            vt.removeObject(transformedEarthObject);
            const indexToRemove = vt.planets.indexOf(transformedEarthObject);
            vt.planets.splice(indexToRemove, 1);
        }

        vt.planetMarkers = vt.planetMarkers || {};
        const earthMarker = vt.planetMarkers[earthId];
        if (earthMarker) {
            vt.getScene().remove(earthMarker);
            earthMarker.geometry?.dispose?.();
            earthMarker.material?.dispose?.();
            delete vt.planetMarkers[earthId];
        }
    },

    removeExistingEarthRotationHelpers() {
        const vt = window["vt"];
        const earthPlaceholderObject = UtilsService.findItemInObjectList(
            "_id",
            SynodicCalculationService.EARTH_PLACEHOLDER_NAME,
            vt.planets
        );
        const earthFakeMoonObject = UtilsService.findItemInObjectList(
            "_id",
            SynodicCalculationService.EARTH_FAKE_MOON_NAME,
            vt.planets
        );
        const helperObjects = [earthPlaceholderObject, earthFakeMoonObject];
        helperObjects.forEach((object) => {
            if (!object) {
                return;
            }
            vt.removeObject(object);
            const indexToRemove = vt.planets.indexOf(object);
            vt.planets.splice(indexToRemove, 1);
        });
    },

    calculateAveragedEarthOrbitPoints() {
        const radians = Math.PI / 180;
        const averagedEarthOrbitPoints = [];
        for (let angle = 0; angle <= 360; angle += SynodicCalculationService.AVERAGED_EARTH_ORBIT_STEP_ANGLE) {
            const x = SynodicCalculationService.AVERAGED_EARTH_ORBIT_RADIUS * Math.cos(angle * radians);
            const y = SynodicCalculationService.AVERAGED_EARTH_ORBIT_RADIUS * Math.sin(angle * radians);
            const z = 0;
            averagedEarthOrbitPoints.push([x, y, z]);
        }
        return averagedEarthOrbitPoints;
    },

    checkIsFocusOnObject(designator) {
        const isFocusOnObject = VisualisationService.getSovtFocusedObject() === designator;
        if (!isFocusOnObject) {
            return;
        }
        const vt = window["vt"];
        if (!vt) {
            return;
        }
        vt.getViewer().stopFollowingObject();
        SpacekitService.focusOnSovtObject(designator);
    },

    isObjectFocused(designator) {
        return VisualisationService.getSovtFocusedObject() === designator;
    },

    adjustDetectionPolarObjectPosition() {
        const detectionPolarMergedObject = SpacekitService.getSceneObjectByName("detectionPolarMergedOutside");
        if (detectionPolarMergedObject) {
            return;
        }
        const transformedEarthPosition = SynodicCalculationService.getTransformedEarthObjectPosition();
        if (!transformedEarthPosition) {
            return;
        }
        SynodicCalculationService.setDetectionPolarObjectPosition(transformedEarthPosition);
    },

    adjustDetectionPolarMergedPosition() {
        const detectionPolarMergedObject = SpacekitService.getSceneObjectByName("detectionPolarMergedOutside");
        if (!detectionPolarMergedObject) {
            return;
        }
        const transformedEarthPosition = SynodicCalculationService.getTransformedEarthObjectPosition();
        if (!transformedEarthPosition) {
            return;
        }
        SpacekitService.updateMergedDetectionPolar();
    },

    getTransformedEarthObjectPosition() {
        const vt = window["vt"];
        if (!vt || !vt.planets || !vt.planets.length) {
            return null;
        }
        const transformedEarthObject = UtilsService.findItemInObjectList(
            "_id",
            SynodicCalculationService.TRANSFORMED_EARTH_OBJECT_NAME,
            vt.planets
        );
        if (!transformedEarthObject) {
            return null;
        }
        const currentTime = UtilsService.dateToJulian(VisualisationService.getSimulationTime());
        return transformedEarthObject.getPosition(currentTime);
    },

    setDetectionPolarObjectPosition(transformedEarthPostion) {
        const detectionPolarObject = SpacekitService.getSceneObjectByName("detectionPolar");
        if (!detectionPolarObject) {
            return;
        }
        detectionPolarObject.position.setX(transformedEarthPostion[0] - 1);
    },

    getActiveObject(designator) {
        const spaceObjects = window["vt"].spaceObjects;
        const availableIds = SynodicCalculationService.getAvailableSpaceObjectIds(designator);
        return spaceObjects.find(({ _id }) => availableIds.includes(_id));
    },

    calculateObjectDistances(object) {
        if (!window["vt"] || !window["vt"].planets) {
            return { earth: "-", center: "-" };
        }
        const currentDateJd = UtilsService.dateToJulian(VisualisationService.getSimulationTime());
        const planetsObjects = window["vt"].planets;
        const earthObject = planetsObjects.find(
            ({ _id }) => _id === SynodicCalculationService.TRANSFORMED_EARTH_OBJECT_NAME
        );
        const objectPosition = object.getPosition(currentDateJd);
        const earthPosition = earthObject.getPosition(currentDateJd);
        return {
            earth: VisualisationService.distanceToBody(objectPosition, earthPosition),
            center: VisualisationService.distanceToCenter(objectPosition),
        };
    },

    toggleEarthOnScale(isEarthOnScale) {
        const vt = window["vt"];
        const transformedEarthObject = UtilsService.findItemInObjectList(
            "_id",
            SynodicCalculationService.TRANSFORMED_EARTH_OBJECT_NAME,
            vt.planets
        );
        if (!transformedEarthObject) {
            return;
        }
        const earthScale = transformedEarthObject._object3js.scale;
        earthScale.setScalar(isEarthOnScale ? SynodicCalculationService.EARTH_ON_SCALE_FACTOR : 1);
        SynodicCalculationService.checkIsFocusOnObject(SynodicCalculationService.TRANSFORMED_EARTH_OBJECT_NAME);
        const isDetectionPolarVisible =
            SynodicCalculationService.getSelectedCalculatedObject() &&
            VisualisationService.getSettings().objectsSettings.detectionPolar.value;
        if (isDetectionPolarVisible) {
            SynodicCalculationService.adjustDetectionPolarObjectPosition();
            SynodicCalculationService.adjustDetectionPolarMergedPosition();
        }
        const isElongationConeVisible = VisualisationService.getSettings().objectsSettings.elongationCone.value;
        if (isElongationConeVisible) {
            SynodicCalculationService.adjustElongationConePosition();
        }
    },

    checkIsFocusOnAsteroid(isEarthOnScale) {
        const focusedObject = VisualisationService.getSovtFocusedObject();
        const nonAsteroidFocus = ["sun", SynodicCalculationService.TRANSFORMED_EARTH_OBJECT_NAME];
        const isFocusOnAsteroid = focusedObject && !nonAsteroidFocus.includes(focusedObject);
        if (!isFocusOnAsteroid) {
            return;
        }
        const asteroidZoomFactor = isEarthOnScale ? SynodicCalculationService.ASTEROID_ZOOM_FACTOR : 1;
        SpacekitService.setMinCameraDistance(0.004 * asteroidZoomFactor);
    },

    getAvailableSpaceObjectIds(designator) {
        return [
            `${designator}${SynodicCalculationService.TRANSFORMED_SPACE_OBJECT_SUFFIX}`,
            `${designator}${SynodicCalculationService.TRANSFORMED_SPACE_OBJECT_SUFFIX_PERTURBED}`,
        ];
    },

    getCurrentTimeframeLength() {
        const { startDateJulian, endDateJulian } = SynodicCalculationService.getCurrentJulianTimeframe();
        return endDateJulian - startDateJulian + 1;
    },

    getCurrentJulianTimeframe() {
        const { start: startDateJulian, end: endDateJulian } = TimeFrameService.getTimeFrame().converted;
        return { startDateJulian, endDateJulian };
    },

    getStringTimeframe() {
        const { start, end } = TimeFrameService.getTimeFrame();
        return {
            startDateString: `${start}T00:00Z`,
            endDateString: `${end}T00:00Z`,
        };
    },

    calculateTransfomredEarthPointData(earthObject, dayJulian) {
        const earthPosition = earthObject.getPosition(dayJulian);
        const [, , zEarth] = earthPosition;
        const fixedEarthCenterDistance = +VisualisationService.distanceToCenter(earthPosition, 5);
        return { x: fixedEarthCenterDistance, y: 0, z: zEarth };
    },

    createEphemeridesTable(objectPoints) {
        const objectData = objectPoints.map((pointData) => [
            +pointData.epoch,
            +pointData.xa,
            +pointData.ya,
            +pointData.za,
            0,
            0,
            0,
        ]);

        // Only include orbitalPeriods if at least one is defined
        const orbitalPeriods = objectPoints.map(point =>
            point.orbitalPeriod !== null && point.orbitalPeriod !== undefined
                ? parseFloat(point.orbitalPeriod)
                : null
        );

        const objectEphemeridesTable = {
            data: objectData,
            distanceUnits: "au",
            timeUnits: "day",
        };

        const table = new Spacekit.EphemerisTable(objectEphemeridesTable);

        // Attach only if valid values exist
        if (orbitalPeriods.some(v => v !== null && !isNaN(v))) {
            table.orbitalPeriods = orbitalPeriods;
        }

        return table;
    },

    addTransformedEarthObject(earthEphemeridesTable, fakeEarthEphemeridesTable) {
        const vt = window["vt"];
        const objectsSettings = VisualisationService.getSettings().objectsSettings;
        const isEarthOnScale = objectsSettings.earthOnScale.value;
        const earthScaleFactor = isEarthOnScale ? SynodicCalculationService.EARTH_ON_SCALE_FACTOR : 1;
        const transformedEarthObjectOptions = {
            ...SynodicCalculationService.EARTH_OBJECT_DEFAULT_OPTIONS,
            ephemTable: earthEphemeridesTable,
            labelLayer: LayerService.getToolLayers().EARTH_NAME,
            hideOrbit: true,
        };
        const transformedEarthObject = vt.createSphere(
            SynodicCalculationService.TRANSFORMED_EARTH_OBJECT_NAME,
            transformedEarthObjectOptions
        );
        transformedEarthObject._object3js.name = SynodicCalculationService.TRANSFORMED_EARTH_OBJECT_NAME;
        transformedEarthObject._object3js.scale.setScalar(earthScaleFactor);
        vt.planets.push(transformedEarthObject);
        HUDService.createLabel(
            transformedEarthObject,
            UtilsService.hexColor(objectsSettings.earthName.color),
            objectsSettings.earthNameSize.value
        );

        vt.planetMarkers = vt.planetMarkers || {};
        const earthId = SynodicCalculationService.TRANSFORMED_EARTH_OBJECT_NAME;
        if (vt.planetMarkers[earthId]) {
            const old = vt.planetMarkers[earthId];
            vt.getScene().remove(old);
            old.geometry?.dispose?.();
            old.material?.dispose?.();
            delete vt.planetMarkers[earthId];
        }
        // --- SOVT Earth marker (tiny ultramarine-blue sphere) ---
        vt.planetMarkers = vt.planetMarkers || {};
        const geometry = new THREE.SphereGeometry(0.02, 16, 16);
        const material = new THREE.MeshBasicMaterial({
            color: 0x1878bd,       // ultramarine blue
            transparent: true,
            opacity: 1.0,
            depthTest: false,
            depthWrite: false,
        });
        const earthMarker = new THREE.Mesh(geometry, material);
        earthMarker.frustumCulled = false;
        earthMarker.renderOrder = 9999;
        earthMarker.name = `marker_${SynodicCalculationService.TRANSFORMED_EARTH_OBJECT_NAME}`;

        // place it at Earth's current position (tracking comes next step)
        const jdNow = vt.getJd ? vt.getJd() : UtilsService.dateToJulian(VisualisationService.getSimulationTime());
        const [ex, ey, ez] = transformedEarthObject.getPosition(jdNow);
        earthMarker.position.set(ex, ey, ez);

        vt.getScene().add(earthMarker);
        vt.planetMarkers[SynodicCalculationService.TRANSFORMED_EARTH_OBJECT_NAME] = earthMarker;
        // --- end Earth marker ---

        SynodicCalculationService.createEarthRotationHelpers(fakeEarthEphemeridesTable);
    },

    createEarthRotationHelpers(fakeEarthEphemeridesTable) {
        const vt = window["vt"];
        const fakeMoonObject = vt.createSphere(SynodicCalculationService.EARTH_FAKE_MOON_NAME, {
            ephem: new Spacekit.Ephem(SynodicCalculationService.FAKE_MOON_DEFAULT_OPTIONS.ephemData),
            ...SynodicCalculationService.FAKE_MOON_DEFAULT_OPTIONS.data,
            name: SynodicCalculationService.EARTH_FAKE_MOON_NAME,
        });
        const earthPlaceholder = vt.createObject(SynodicCalculationService.EARTH_PLACEHOLDER_NAME, {
            name: SynodicCalculationService.EARTH_PLACEHOLDER_NAME,
            textureUrl: "/static/textures/particle.png",
            scale: [0.01, 0.01, 0.01],
            theme: {
                color: 0xff0000,
            },
            ephemTable: fakeEarthEphemeridesTable,
        });
        fakeMoonObject.orbitAround(earthPlaceholder);
        const helperObjects = [fakeMoonObject, earthPlaceholder];
        helperObjects.forEach((object) => {
            object.getOrbit().setVisibility(false);
            object._object3js.visible = false;
            vt.planets.push(object);
        });
        const earth = UtilsService.findItemInObjectList(
            "_id",
            SynodicCalculationService.TRANSFORMED_EARTH_OBJECT_NAME,
            vt.planets
        );
        const earthTargetPosition = fakeMoonObject.getPosition(vt.getJd());
        const targetVector = new THREE.Vector3(earthTargetPosition[0], earthTargetPosition[1], earthTargetPosition[2]);
        earth._object3js.lookAt(targetVector);
    },

    getSelectedSynodicObjectParameters() {
        const selectedObject = VisualisationService.getSelectedSynodicObjectName();
        if (!selectedObject) {
            return;
        }
        const apiUrl = SynodicCalculationService.getEphemeridesUrl(selectedObject);
        const loadingObjects = SynodicCalculationService.loadingObjectsEphemerides();
        loadingObjects.push(selectedObject);
        return axios(apiUrl).then((response) => response.data.ephemerides);
    },

    getPerturbedSynodicOrbitParameters(objectName, objectType = null) {
        const apiUrl = SynodicCalculationService.getPerturbedUrl(objectName, objectType);
        //const apiUrl ="http://gui.deimos-space.com/rest/nov/synodic/mockup_with_orbital_period.json"; // for mockup
        //console.log(`Fetching perturbed orbit parameters for ${objectName} from ${apiUrl}`);
        const loadingObjects = SynodicCalculationService.loadingObjectsEphemerides();

        if (!loadingObjects.includes(objectName)) {
            loadingObjects.push(objectName);
        }
        return axios(apiUrl)
            .then((response) => {
                const data = response.data;
                // ALWAYS remove from loading list
                const idx = loadingObjects.indexOf(objectName);
                if (idx !== -1) {
                    loadingObjects.splice(idx, 1);
                }
                return data;
            })
            .catch((error) => {
                console.log(error);
                // ALSO remove from loading list on error
                const idx = loadingObjects.indexOf(objectName);
                if (idx !== -1) {
                    loadingObjects.splice(idx, 1);
                }

                // propagate "no data" to caller
                return null;
            });
    },

    getImpactorsList() {
        const apiUrl = `${Config.api.restUrl}${Config.api.impactors}`;
        return axios(apiUrl)
            .then((response) => {
                const formattedData = response.data.map((impactorInfo) => ({
                    designator: SynodicCalculationService.getDesignator(impactorInfo),
                    date: impactorInfo.impactDate,
                }));
                return formattedData;
            })
            .catch((error) => {
                const message = UtilsService.prepareErrorMessage(error);
                PopupService.show({
                    component: "PopupInfo",
                    type: "error",
                    message,
                });
            });
    },

    checkImpactDate(designator) {
        const impactorsList = SynodicCalculationService.getImpactors();
        const impactorObject = UtilsService.findItemInObjectList("designator", designator, impactorsList);
        if (!impactorObject) {
            return null;
        }
        const { start, end } = TimeFrameService.getTimeFrame();
        const startTimestamp = new Date(start).getTime();
        const endTimestamp = new Date(end).getTime();
        const impactTimestamp = new Date(impactorObject.date).getTime();
        const isImpactInSelectedDates = impactTimestamp <= endTimestamp && impactTimestamp >= startTimestamp;
        return isImpactInSelectedDates ? impactorObject.date : null;
    },

    verifyCalculatedRealSynodicObjectsList(finalAsteroidsList) {
        SynodicCalculationService.verifyCalculatedObjects(finalAsteroidsList);
        SynodicCalculationService.verifyVisibleObjects(finalAsteroidsList);
        SynodicCalculationService.verifyPerturbedOrbitList(finalAsteroidsList);
    },

    verifyCalculatedObjects(finalAsteroidsList) {
        const calculatedObjects = UtilsService.deepCopy(
            SynodicCalculationService.getCalculatedRealSynodicObjectsList()
        );
        const selectedSynodicObjectName = VisualisationService.getSelectedSynodicObjectName();
        const filteredCalculatedObjects = calculatedObjects.filter((calculatedObject) => {
            const isSyntheticObject = SyntheticObjectsService.isSyntheticObject(calculatedObject.designator);
            return (
                isSyntheticObject ||
                SynodicCalculationService.checkIsObjectOnList(finalAsteroidsList, calculatedObject.designator)
            );
        });
        const isSelectedCalculated = UtilsService.findItemInObjectList(
            "designator",
            selectedSynodicObjectName,
            filteredCalculatedObjects
        );
        !isSelectedCalculated && VisualisationService.selectSynodicObject(selectedSynodicObjectName);
        SynodicCalculationService.setCalculatedRealSynodicObjectsList(filteredCalculatedObjects);
    },

    verifyVisibleObjects(finalAsteroidsList) {
        const filteredVisibleObjectsList = [];
        const visibleObjectsToRemoveList = [];
        const visibleObjectsList = SynodicCalculationService.getVisibleRealSynodicObjectsList();
        visibleObjectsList.forEach((visibleObjectDesignator) => {
            const isSyntheticObject = SyntheticObjectsService.isSyntheticObject(visibleObjectDesignator);
            return isSyntheticObject ||
                SynodicCalculationService.checkIsObjectOnList(finalAsteroidsList, visibleObjectDesignator)
                ? filteredVisibleObjectsList.push(visibleObjectDesignator)
                : visibleObjectsToRemoveList.push(visibleObjectDesignator);
        });
        if (!visibleObjectsToRemoveList.length) {
            return;
        }
        visibleObjectsToRemoveList.forEach((visibleObjectDesignator) => {
            const isFocusedRemoved = SynodicCalculationService.isObjectFocused(visibleObjectDesignator);
            isFocusedRemoved && VisualisationService.setSovtFocusedObject('');
            SynodicCalculationService.removeAllExistingSpaceObjects(visibleObjectDesignator);
            SynodicChartService.removeSynodicOrbitObjectChartData(visibleObjectDesignator);
        });
        SynodicCalculationService.setVisibleRealSynodicObjectsList(filteredVisibleObjectsList);
    },

    verifyPerturbedOrbitList(finalAsteroidsList) {
        const perturbedOrbitList = SynodicCalculationService.getPerturbedSynodicOrbitList();
        const filteredPerturbedOrbitList = perturbedOrbitList.filter((visibleObjectDesignator) => {
            const isSyntheticObject = SyntheticObjectsService.isSyntheticObject(visibleObjectDesignator);
            return (
                isSyntheticObject ||
                SynodicCalculationService.checkIsObjectOnList(finalAsteroidsList, visibleObjectDesignator)
            );
        });
        SynodicCalculationService.setPerturbedSynodicOrbitList(filteredPerturbedOrbitList);
    },

    checkIsObjectOnList(finalAsteroidsList, objectDesignator) {
        return finalAsteroidsList.some(
            (asteroid) => SynodicCalculationService.getDesignator(asteroid) === objectDesignator
        );
    },

    getSynodicObjectVisibilityInfo(designator) {
        const visibleObjectsList = SynodicCalculationService.getVisibleRealSynodicObjectsList();
        const objectIndexInVisibleList = visibleObjectsList.indexOf(designator);
        const isObjectVisible = objectIndexInVisibleList !== -1;
        const isObjectSelected = designator === VisualisationService.getSelectedSynodicObjectName();
        return { isObjectSelected, isObjectVisible, objectIndexInVisibleList };
    },

    updateVisibleRealSynodicObjectsList(designator) {
        const visibleObjects = SynodicCalculationService.getVisibleRealSynodicObjectsList();
        const isInvisible = !visibleObjects.includes(designator);
        if (isInvisible) {
            visibleObjects.push(designator);
            SynodicCalculationService.setVisibleRealSynodicObjectsList(visibleObjects);
        }
    },

    getSelectedCalculatedObject() {
        const selectedObjectDesignator = VisualisationService.getSelectedSynodicObjectName();
        if (!selectedObjectDesignator) {
            return null;
        }
        const calculatedObjectsList = SynodicCalculationService.getCalculatedRealSynodicObjectsList();
        return UtilsService.findItemInObjectList("designator", selectedObjectDesignator, calculatedObjectsList);
    },

    calculateOrbitPeriod() {
        const activeObject = SynodicCalculationService.getSynodicActiveObject();
        const calculatedObject = SynodicCalculationService.getSelectedCalculatedObject();
        if (!activeObject || !calculatedObject || !calculatedObject.objectData) {
            return null;
        }

        const designator = activeObject._id.split("_")[0];
        const isPerturbedOrbit = SynodicCalculationService.getPerturbedSynodicOrbitList().includes(designator);
        const currentDateJd = UtilsService.dateToJulian(VisualisationService.getSimulationTime());
        const ephem = activeObject._orbit?.ephem;

        if (
            isPerturbedOrbit &&
            ephem &&
            Array.isArray(ephem.orbitalPeriods) &&
            ephem.orbitalPeriods.some(v => v !== null && !isNaN(v))
        ) {
            // Use interpolated orbital period if available
            const orbitalPeriod = this.getInterpolatedOrbitalPeriod(currentDateJd, ephem);
            return isNaN(orbitalPeriod) ? null : orbitalPeriod;
        } else {
            // Fallback to Keplerian estimate
            if (!calculatedObject.objectData.orbitPeriod && calculatedObject.objectData.a) {
                return UtilsService.estimateOrbitPeriod(calculatedObject.objectData.a);
            }
            return calculatedObject.objectData.orbitPeriod
                ? calculatedObject.objectData.orbitPeriod / SynodicCalculationService.EARTH_PERIOD
                : null;
            }
    },

    calculateSynodicOrbitPeriod() {
        const activeObject = SynodicCalculationService.getSynodicActiveObject();
        const calculatedObject = SynodicCalculationService.getSelectedCalculatedObject();
        const orbitPeriod = this.calculateOrbitPeriod();
        if (!activeObject || !calculatedObject || orbitPeriod === null) {
            return null;
        }
        if (orbitPeriod === 1) {
            return 1;
        }
        return Math.abs(1 / (1 - 1 / orbitPeriod));
    },

    calculateElongationAngle() {
        const activeObject = SynodicCalculationService.getSynodicActiveObject();
        const calculatedObject = SynodicCalculationService.getSelectedCalculatedObject();
        if (!activeObject || !calculatedObject) {
            return null;
        }
        const currentDateJd = UtilsService.dateToJulian(VisualisationService.getSimulationTime());

        const [xObject, yObject] = activeObject.getPosition(currentDateJd);

        if (!window['vt'] || !window['vt'].planets) {
            return null;
        }
        const transformedEarthObject = UtilsService.findItemInObjectList(
            "_id",
            SynodicCalculationService.TRANSFORMED_EARTH_OBJECT_NAME,
            window['vt'].planets
        );
        if (!transformedEarthObject) {
            return null;
        }
        const [xTransformedEarth] = transformedEarthObject.getPosition(currentDateJd);
        const elongationAngle = Math.atan2(yObject, xTransformedEarth - xObject) * 180/Math.PI;

        return isNaN(elongationAngle) ? null : elongationAngle;
    },

    getInterpolatedOrbitalPeriod(jd, ephem) {

        if (!ephem || !ephem.data || !ephem.orbitalPeriods) {
            console.warn("Ephemeris data or orbital periods are missing");
            return null;
        }
        const data = ephem.data;
        const orbitalPeriods = ephem.orbitalPeriods;

        for (let i = 0; i < data.length - 1; i++) {
            const jd1 = data[i][0];
            const jd2 = data[i + 1][0];

            if (jd >= jd1 && jd <= jd2) {
                const t = (jd - jd1) / (jd2 - jd1);
                const op1 = orbitalPeriods[i];
                const op2 = orbitalPeriods[i + 1];

                if (isNaN(op1) || isNaN(op2)) return null;

                return (1 - t) * op1 + t * op2;
            }
        }

        return null;
    },

    getCurrentChartValue(propertyName) {
        const activeObject = SynodicCalculationService.getSynodicActiveObject();
        const calculatedObject = SynodicCalculationService.getSelectedCalculatedObject();
        if (!activeObject || !calculatedObject) {
            return null;
        }
        const { date, objects } = SynodicChartService.getSynodicOrbitChartsData();
        const isPerturbedOrbit = SynodicCalculationService.getPerturbedSynodicOrbitList().includes(
            calculatedObject.designator
        );
        const objectDesignator = isPerturbedOrbit
            ? `${calculatedObject.designator}_perturbed`
            : calculatedObject.designator;
        if (!objects[objectDesignator]) {
            return null;
        }
        const currentTime = VisualisationService.getSimulationTime();
        const currentDateString = moment.utc(currentTime).format("YYYY-MM-DD");
        const currentTimestamp = moment.utc(currentDateString, "YYYY-MM-DD").valueOf();
        const dataIndex = date.findIndex((date) => date === currentTimestamp);
        if (dataIndex === -1) {
            return null;
        }
        const chartData = objects[objectDesignator][propertyName];
        if (!chartData || chartData[dataIndex] === undefined || chartData[dataIndex] === null) {
            return null;
        }
        return chartData[dataIndex];
    },

    toogleElongationCone(isVisible) {
        isVisible ? SynodicCalculationService.showElongationCone() : SynodicCalculationService.hideElongationCone();
    },

    showElongationCone() {
        const elongationConeObjects = SynodicCalculationService.getElongationConeObjects();
        if (!elongationConeObjects.length) {
            SynodicCalculationService.createElongationConeObject();
            return;
        }
        elongationConeObjects.forEach((object) => (object.visible = true));
        SynodicCalculationService.adjustElongationConePosition();
    },

    hideElongationCone() {
        const elongationConeObjects = SynodicCalculationService.getElongationConeObjects();
        elongationConeObjects.forEach((object) => (object.visible = false));
    },

    createElongationConeObject() {
        const objectsSettings = VisualisationService.getSettings().objectsSettings;
        const coneProperties = objectsSettings.elongationConeProperties.children;
        const color = SynodicCalculationService.getElongationConeColor();
        const transparency = coneProperties.transparency.value;
        const halfAngle = coneProperties.coneAngle.value;
        const { height, radius } = SynodicCalculationService.calculateElongationConeDimensions(halfAngle);
        const transformedEarthPosition = SynodicCalculationService.getTransformedEarthObjectPosition();
        const xConePosition = transformedEarthPosition[0] - height / 2;
        const isDetectionPolarVisible =
            objectsSettings.detectionPolar.value && SpacekitService.getDetectionPolarObjects().length;
        const coneMaterial = new THREE.MeshLambertMaterial({
            color,
            transparent: true,
            opacity: 1 - transparency,
            depthFunc: isDetectionPolarVisible ? THREE.GreaterDepth : THREE.LessEqualDepth,
            side: THREE.DoubleSide,
        });
        const coneGeometry = new THREE.ConeGeometry(radius, height, 64);
        const coneObject = new THREE.Mesh(coneGeometry, coneMaterial);
        coneObject.rotateZ((-90 * Math.PI) / 180);
        coneObject.position.setX(xConePosition);
        coneObject.renderOrder = 5;
        coneObject.name = "elonagtionCone";
        window["vt"].getScene().add(coneObject);
    },

    changeElongationConeAngle(halfAngle) {
        const elongationConeObjects = SynodicCalculationService.getElongationConeObjects();
        if (!elongationConeObjects.length) {
            return;
        }
        const transformedEarthPosition = SynodicCalculationService.getTransformedEarthObjectPosition();
        const { height, radius } = SynodicCalculationService.calculateElongationConeDimensions(halfAngle);
        const xConePosition = transformedEarthPosition[0] - height / 2;
        elongationConeObjects.forEach((object) => {
            object.geometry.dispose();
            object.geometry = new THREE.ConeGeometry(radius, height, 64);
            object.position.setX(xConePosition);
        });
    },

    changeElongationConeTransparency(transparency) {
        const elongationConeObjects = SynodicCalculationService.getElongationConeObjects();
        if (!elongationConeObjects.length) {
            return;
        }
        elongationConeObjects.forEach((object) => (object.material.opacity = 1 - transparency));
    },

    changeElongationConeColor(color) {
        const elongationConeObjects = SynodicCalculationService.getElongationConeObjects();
        if (!elongationConeObjects.length) {
            return;
        }
        elongationConeObjects.forEach((object) =>
            object.material.color.set(UtilsService.convertHexColorToHexNumber(color))
        );
    },

    calculateElongationConeDimensions(halfAngle) {
        const defaultHeight = 3;
        const radians = Math.PI / 180;
        if (halfAngle <= 75) {
            const radius = Math.tan(halfAngle * radians) * defaultHeight;
            return { height: defaultHeight, radius };
        }
        const hypotenuse = defaultHeight / Math.cos(75 * radians);
        const height = hypotenuse * Math.cos(halfAngle * radians);
        const radius = Math.tan(halfAngle * radians) * height;
        return { height, radius };
    },

    adjustElongationConePosition() {
        const elongationConeObjects = SynodicCalculationService.getElongationConeObjects();
        if (!elongationConeObjects.length) {
            return;
        }
        const transformedEarthPosition = SynodicCalculationService.getTransformedEarthObjectPosition();
        elongationConeObjects.forEach((object) => {
            const height = object.geometry.parameters.height;
            const xConePosition = transformedEarthPosition[0] - height / 2;
            object.position.setX(xConePosition);
        });
    },

    changeElongationConeAppearance(isDetectionPolarVisible = false) {
        const elongationConeObjects = SynodicCalculationService.getElongationConeObjects();
        if (!elongationConeObjects.length) {
            return;
        }
        const isVisible = isDetectionPolarVisible && SpacekitService.getDetectionPolarObjects().length;
        elongationConeObjects.forEach(
            (object) => (object.material.depthFunc = isVisible ? THREE.GreaterDepth : THREE.LessEqualDepth)
        );
    },

    getElongationConeColor() {
        const settings = VisualisationService.getSettingsDefaults();
        const color = settings.objectsSettings.elongationConeProperties.children.color.value;
        return UtilsService.convertHexColorToHexNumber(color);
    },

    getElongationConeObjects() {
        const elongationConeNames = ["elonagtionCone"];
        return elongationConeNames
            .map((name) => SpacekitService.getSceneObjectByName(name))
            .filter((elongationConeObject) => elongationConeObject);
    },

    adjustSovtToolState(focusedObject) {
        SynodicCalculationService.createTransformedEarthObject();
        SynodicCalculationService.recalculateVisibleSynodicOrbits();
        SynodicCalculationService.recalculateTransformedPlanets();
        const isFocusedObject = focusedObject && focusedObject !== "sun";
        isFocusedObject && VisualisationService.setSovtFocusedObject(focusedObject);
    },

    getEphemeridesUrl(objectName) {
        return `${Config.api.restUrl}${Config.api.getEphemerides}keplerian?names=${objectName}`;
    },

    getPerturbedUrl(objectName, objectType = null) {
        const apiParameters = SyntheticObjectsService.getSyntheticObjectSovtApiParameters(objectName, objectType);
        const api = (objectType === SyntheticObjectsService.OBJECT_TYPES.hyperbolic) ? `${Config.api.synodicByHyperbolic}` : `${Config.api.synodic}`;
        return `${Config.api.restUrl}` + api + UtilsService.parseParams(apiParameters);
    },

    getDesignator(object) {
        return (object.number ? object.number : object.name).toString();
    },

    getObjectParametersMap(objectType = null) {
        if (objectType === SyntheticObjectsService.OBJECT_TYPES.hyperbolic) {
            return {
                epoch: "referenceEpoch",
                q: "perihelionDistance",
                i: "inclination",
                om: "ascendingNodeLongitude",
                w: "perihelionArgument",
                tp: "pericentrePassage",
                mag: "absoluteMagnitude",
                g: "slopeParameter",
            };
        } else {
            return {
                epoch: "referenceEpoch",
                q: "perihelionDistance",
                aphelion: "aphelionDistance",
                i: "inclination",
                om: "rightAscension",
                w: "perihelionArgument",
                ma: "meanAnomaly",
                mag: "absoluteMagnitude",
                g: "slopeParameter",
            };
        }
    },

    hasPerturbedPoints(pointsData) {
        // If backend failed and we have no data
        if (!pointsData || typeof pointsData !== "object") {
            return false;
        }

        const values = Object.values(pointsData);
        if (!values.length || !values[0]) {
            return false;
        }

        return !!values[0].asteroid;
    },

    isDateChanged(designator) {
        const synodicPerturbedData = this.getSynodicPerturbedData();

        // Find cached object
        const foundObject = Array.isArray(synodicPerturbedData) ? synodicPerturbedData.find(obj => obj.designator === designator) : undefined;


        if (!foundObject) {
            return true; // No previous data → Force fetch
        }

        // Get currently selected date range
        const { startDateJulian, endDateJulian } = this.getCurrentJulianTimeframe();
        const keys = Object.keys(foundObject.data).sort((a, b) => parseFloat(a) - parseFloat(b));

        // Get the date and transform it to Julian date
        const indexStart = 0;
        const keyAtIndexStart = keys[indexStart];
        const firstCachedDate = Math.trunc(parseFloat(keyAtIndexStart)) + 2400000.5;

        const indexEnd = Object.keys(foundObject.data).length - 1;
        const keyAtIndexEnd = keys[indexEnd];
        const lastCachedDate = Math.trunc(parseFloat(keyAtIndexEnd)) + 2400000.5;

        return !(firstCachedDate === startDateJulian && lastCachedDate === endDateJulian);
    },

    showNoPerturbedInfo(designator) {
        PopupService.show({
            component: "PopupInfo",
            type: "warning",
            message: Keys.t.perturbedOrbitForObject + ' ' + designator + ' ' + Keys.t.doesNotExist + '.'
        });
    },

    showNoEphemeridesData(designator) {
        PopupService.show({
            component: "PopupInfo",
            type: "warning",
            message: Keys.t.ephemeridesData.replace("{objectName}", designator),
        });
    },

    loadingObjectsEphemerides() {
        return store.getters[CommonGetters.loadingObjectsEphemerides];
    },

    getPerturbedSynodicOrbitList() {
        return store.getters[CommonGetters.perturbedSynodicOrbitList];
    },

    setPerturbedSynodicOrbitList(perturbedSynodicOrbitList) {
        return store.dispatch(CommonActions.setPerturbedSynodicOrbitList, perturbedSynodicOrbitList);
    },

    getSynodicPerturbedData(){
        return store.getters[CommonGetters.synodicPerturbedData];
    },
    setSynodicPerturbedData(object){
        store.dispatch(CommonActions.setSynodicPerturbedData, object);
    },

    getCalculatedRealSynodicObjectsList() {
        return store.getters[CommonGetters.calculatedRealSynodicObjectsList];
    },

    setCalculatedRealSynodicObjectsList(calculatedRealSynodicObjectsList) {
        return store.dispatch(CommonActions.setCalculatedRealSynodicObjectsList, calculatedRealSynodicObjectsList);
    },

    getVisibleRealSynodicObjectsList() {
        return store.getters[CommonGetters.visibleRealSynodicObjectsList];
    },

    setVisibleRealSynodicObjectsList(visibleRealSynodicObjectsList) {
        return store.dispatch(CommonActions.setVisibleRealSynodicObjectsList, visibleRealSynodicObjectsList);
    },

    setSynodicActiveObject(activeObject) {
        if (activeObject) {
            const newObject = Object.create(Object.getPrototypeOf(activeObject));
            store.dispatch(CommonActions.setSynodicActiveObject, Object.assign(newObject, activeObject));
        } else {
            store.dispatch(CommonActions.setSynodicActiveObject, activeObject);
        }
    },

    getSynodicActiveObject() {
        return store.getters[CommonGetters.synodicActiveObject];
    },

    setDetectionPolarPoints(points) {
        store.dispatch(CommonActions.setDetectionPolarPoints, points);
    },

    getDetectionPolarPoints() {
        return store.getters[CommonGetters.detectionPolarPoints];
    },

    setSynodicTimelineSpeed(speed) {
        store.dispatch(CommonActions.setSynodicSpeed, speed);
    },

    getSynodicTimelineSpeed() {
        return store.getters[CommonGetters.synodicSpeed];
    },

    setImpactors(impactorsList) {
        store.dispatch(CommonActions.setImpactorsList, impactorsList);
    },

    getImpactors() {
        return store.getters[CommonGetters.impactorsList];
    },

};

export default SynodicCalculationService;
